<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PlayerIconButton from '@/components/quran/player/PlayerIconButton.vue';
import PlayerProgress from '@/components/quran/player/PlayerProgress.vue';
import PlayerRecitation from '@/components/quran/player/PlayerRecitation.vue';
import PlayerSleepTimer from '@/components/quran/player/PlayerSleepTimer.vue';
import PlayerSpeed from '@/components/quran/player/PlayerSpeed.vue';
import PlayerTransport from '@/components/quran/player/PlayerTransport.vue';
import PlayerVolume from '@/components/quran/player/PlayerVolume.vue';
import ReciterPicker from '@/components/quran/ReciterPicker.vue';
import SurahArtwork from '@/components/quran/SurahArtwork.vue';
import UiContainer from '@/components/ui/UiContainer.vue';
import UiSegmented from '@/components/ui/UiSegmented.vue';
import { useAsyncData } from '@/composables/useAsyncData';
import { useCopyFeedback } from '@/composables/useCopyFeedback';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';
import { useQuranSettings } from '@/composables/quran/useQuranSettings';
import { fetchSurahs } from '@/utils/quran/api';
import { artworkGradient } from '@/utils/quran/artwork';
import { ayahPath } from '@/utils/quran/paths';
import { SITE_URL } from '@/seo/siteContent.js';

/**
 * The full-screen player: the surah's cover art and the controls on one side,
 * the recitation as running text on the other. The whole screen takes the
 * surah's colours, so every surah has its own room.
 */
const emit = defineEmits(['collapse', 'open-surah']);

const { t, locale } = useI18n();
const player = useQuranPlayer();
const settings = useQuranSettings();
const state = player.state;
const current = player.current;
const { copied, copy } = useCopyFeedback();

const tab = ref('text');
const tabs = computed(() => [
  { value: 'text', label: t('quran.player.tabText'), icon: 'solar:document-text-linear' },
  { value: 'reciter', label: t('quran.player.reciter'), icon: 'solar:microphone-3-linear' },
]);

const { data: surahs } = useAsyncData(() => 'all', fetchSurahs);
const surah = computed(() => surahs.value?.find((s) => s.id === current.value?.surah) ?? null);
const gradient = computed(() => artworkGradient(current.value?.surah ?? 1));

const subtitle = computed(() =>
  t('quran.player.ayahOf', { ayah: current.value?.number, total: surah.value?.ayah_count ?? state.queue.length })
);

async function share() {
  const item = current.value;
  const url = `${SITE_URL}${ayahPath(item.surah, item.number)}`;
  const text = item.translation[locale.value] ?? item.translation.en;
  if (navigator.share) {
    try {
      await navigator.share({ title: `${item.surahName} ${item.key}`, text: `${text}\n— ${item.surahName} ${item.key}`, url });
    } catch {
      // Share sheet dismissed.
    }
    return;
  }
  copy(url);
}
</script>

<template>
  <div
    role="dialog"
    aria-modal="true"
    :aria-label="t('quran.player.label')"
    class="fixed inset-0 z-[70] text-white overflow-hidden"
    :style="{ background: `linear-gradient(160deg, ${gradient[0]} 0%, ${gradient[1]} 55%, #071515 100%)` }"
    @keydown.esc="emit('collapse')"
  >
    <!-- The room: lattice, a gold glow, and the cover art blown up and blurred. -->
    <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
      <svg class="absolute inset-0 w-full h-full opacity-[0.05]">
        <defs>
          <pattern id="full-player-lattice" width="64" height="64" patternUnits="userSpaceOnUse">
            <path d="M32 3l7.3 10.1 12.4-1.9-1.9 12.4L60 32l-10.2 7.3 1.9 12.4-12.4-1.9L32 61l-7.3-10.2-12.4 1.9 1.9-12.4L4 32l10.2-7.4-1.9-12.4 12.4 1.9z" fill="none" stroke="#ecd79f" stroke-width="1" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#full-player-lattice)" />
      </svg>
      <div class="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-deen-gold/10 blur-[140px]" />
      <div class="absolute bottom-0 right-0 w-[700px] h-[500px] rounded-full bg-black/30 blur-[120px]" />
    </div>

    <UiContainer class="relative h-full flex flex-col pt-4 pb-4 lg:pb-8">
      <header class="flex items-center justify-between gap-3 shrink-0">
        <PlayerIconButton icon="solar:alt-arrow-down-linear" :label="t('quran.player.collapse')" @click="emit('collapse')" />
        <div class="text-center min-w-0">
          <p class="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45">{{ t('quran.player.nowPlaying') }}</p>
          <p class="text-sm font-semibold truncate">{{ current.surahName }}</p>
        </div>
        <div class="flex items-center gap-1">
          <PlayerIconButton :icon="copied ? 'solar:check-circle-bold' : 'solar:share-linear'" :label="t('quran.ayah.share')" @click="share" />
          <PlayerIconButton icon="solar:book-2-linear" :label="t('quran.player.openSurah')" @click="emit('open-surah')" />
        </div>
      </header>

      <div class="flex-1 min-h-0 grid lg:grid-cols-12 gap-4 lg:gap-14 mt-3 lg:mt-8">
        <!-- Cover, title and controls. On phones a compact row above the text. -->
        <section class="lg:col-span-5 xl:col-span-4 flex lg:flex-col gap-4 lg:gap-6 items-center lg:items-stretch lg:justify-center shrink-0 order-1">
          <SurahArtwork :surah="current.surah" size="lg" label class="!w-20 lg:!w-full lg:max-w-[400px] lg:mx-auto shrink-0 !rounded-2xl lg:!rounded-[28px]" />
          <div class="min-w-0 lg:text-center">
            <h2 class="font-display text-xl lg:text-4xl font-semibold tracking-tight truncate">{{ current.surahName }}</h2>
            <p class="text-sm text-white/60 truncate">
              <span v-if="surah">{{ surah.translation[locale] ?? surah.translation.en }} · </span>{{ subtitle }}
            </p>
            <p class="hidden lg:block mt-1 text-xs font-semibold text-deen-gold/90">{{ player.reciter.value.name }}</p>
          </div>

          <div class="hidden lg:block space-y-4">
            <PlayerProgress />
            <PlayerTransport size="lg" />
            <div class="flex items-center justify-center gap-3">
              <PlayerSpeed />
              <PlayerSleepTimer placement="top" />
              <PlayerVolume />
            </div>
            <p v-if="state.failed" class="text-center text-sm text-deen-gold">{{ t('quran.player.failed') }}</p>
          </div>
        </section>

        <!-- The recitation, or the reciters. -->
        <section class="lg:col-span-7 xl:col-span-8 min-h-0 flex flex-col order-2 rounded-[28px] lg:bg-black/15 lg:border lg:border-white/10 lg:p-4">
          <UiSegmented v-model="tab" :options="tabs" :label="t('quran.player.label')" tone="dark" size="sm" class="self-start mb-3 shrink-0" />
          <div class="flex-1 min-h-0">
            <PlayerRecitation v-if="tab === 'text'" />
            <div v-else class="h-full overflow-y-auto pr-1">
              <ReciterPicker v-model="settings.reciterId" />
            </div>
          </div>
        </section>

        <!-- Phone: controls fixed under the text. -->
        <section class="lg:hidden order-3 shrink-0 space-y-2">
          <PlayerProgress />
          <PlayerTransport size="lg" />
          <div class="flex items-center justify-center gap-3 pt-1">
            <PlayerSpeed />
            <PlayerSleepTimer placement="top" />
          </div>
          <p v-if="state.failed" class="text-center text-sm text-deen-gold">{{ t('quran.player.failed') }}</p>
        </section>
      </div>
    </UiContainer>
  </div>
</template>
